import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LogOut,
  Users,
  Activity,
  Gamepad2,
  UserPlus,
  RefreshCw,
  ShieldAlert,
  Trash2,
  Clock,
  Database,
  HardDrive,
  ChevronDown,
  ChevronUp,
  Megaphone,
  Sparkles,
} from "lucide-react";
import {
  RetentionCard,
  PlaytimeCard,
  ReferrersCard,
  GamesFunnelCard,
  HeatmapCard,
} from "../components/admin/AdminWidgets";
import MergeDuplicatesCard from "../components/admin/MergeDuplicatesCard";
import { useAppDialog } from "../components/AppDialog";
import AdminShell from "../components/admin/AdminShell";
import AdBanner from "../components/AdBanner";

const BASE = process.env.REACT_APP_BACKEND_URL;
const WINDOW_OPTIONS = [
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
];

// Convert seconds to a compact 'HH h MM m' display, or 'MM m' when under 1h.
const fmtPlaytime = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h === 0) return `${m} min`;
  return `${h}h ${m}m`;
};

// Tiny helper for the dashboard "Live ad slots" preview strip. AdBanner
// renders nothing when a slot has no active ad, so this hint tells the
// admin the slot is empty and points them at the create flow.
const EmptySlotHint = ({ slotId }) => {
  const [empty, setEmpty] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ads?slot=${encodeURIComponent(slotId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!cancelled) setEmpty(!(j && j.ad));
      })
      .catch(() => setEmpty(true));
    return () => {
      cancelled = true;
    };
  }, [slotId]);
  if (empty !== true) return null;
  return (
    <div
      className="text-xs italic mt-1"
      style={{ color: "#8a8a8a" }}
      data-testid={`admin-slot-empty-${slotId}`}
    >
      No active ad — kids see nothing in this slot right now.
    </div>
  );
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AdminDashboard = () => {
  const nav = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading"); // loading|ready|denied
  const [stats, setStats] = useState(null);
  const [suspicious, setSuspicious] = useState({ total: 0, rows: [] });
  const [storage, setStorage] = useState(null);
  const [storageError, setStorageError] = useState("");
  const [retention, setRetention] = useState(null);
  const [playtime, setPlaytime] = useState(null);
  const [referrers, setReferrers] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [windowDays, setWindowDays] = useState(7);
  const [refreshing, setRefreshing] = useState(false);
  const [showBackupSteps, setShowBackupSteps] = useState(false);
  const [error, setError] = useState("");
  const processedRef = useRef(false);
  const { confirm } = useAppDialog();

  const hashSessionId = useMemo(() => {
    const m = location.hash?.match(/session_id=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }, [location.hash]);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;
    const boot = async () => {
      if (hashSessionId) {
        try {
          const r = await fetch(`${BASE}/api/admin/auth/session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ session_id: hashSessionId }),
          });
          const data = await r.json().catch(() => ({}));
          if (!r.ok) {
            setStatus("denied");
            setError(data.detail || "Sign-in failed.");
            return;
          }
          setUser(data.user);
          // Clean the URL fragment so refreshes don't re-exchange.
          window.history.replaceState({}, "", "/admin/dashboard");
        } catch (e) {
          setStatus("denied");
          setError("Network error during sign-in.");
          return;
        }
      } else {
        try {
          const r = await fetch(`${BASE}/api/admin/auth/me`, { credentials: "include" });
          if (!r.ok) {
            nav("/admin", { replace: true });
            return;
          }
          setUser(await r.json());
        } catch {
          nav("/admin", { replace: true });
          return;
        }
      }
      await loadStats();
      setStatus("ready");
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStats = async (nextWindow = windowDays) => {
    setRefreshing(true);
    const w = nextWindow;
    const fetchJson = async (path, setter) => {
      try {
        const r = await fetch(`${BASE}${path}`, { credentials: "include" });
        if (r.ok) setter(await r.json());
      } catch {
        /* best-effort */
      }
    };
    await Promise.all([
      fetchJson("/api/admin/stats", setStats),
      fetchJson("/api/admin/suspicious?limit=10", setSuspicious),
      fetchJson(`/api/admin/retention?days=${w}`, setRetention),
      fetchJson("/api/admin/playtime-distribution", setPlaytime),
      fetchJson("/api/admin/top-referrers?limit=10", setReferrers),
      fetchJson(`/api/admin/games-funnel?days=${w}&limit=15`, setFunnel),
      fetchJson(`/api/admin/hourly-heatmap?days=${w}`, setHeatmap),
      (async () => {
        try {
          const st = await fetch(`${BASE}/api/admin/storage`, { credentials: "include" });
          if (st.ok) {
            setStorage(await st.json());
            setStorageError("");
          } else {
            setStorageError(`Failed to load storage insights (HTTP ${st.status}).`);
          }
        } catch {
          setStorageError("Failed to load storage insights. Check your connection and try again.");
        }
      })(),
    ]);
    setRefreshing(false);
  };

  const changeWindow = async (d) => {
    if (d === windowDays) return;
    setWindowDays(d);
    await loadStats(d);
  };

  const clearSuspicious = async () => {
    const ok = await confirm({
      title: "Clear suspicious log?",
      message: "This wipes every recorded suspicious sign-in attempt. This cannot be undone.",
      tone: "danger",
      confirmLabel: "Clear log",
    });
    if (!ok) return;
    try {
      await fetch(`${BASE}/api/admin/suspicious`, {
        method: "DELETE",
        credentials: "include",
      });
      await loadStats();
    } catch {
      /* ignore */
    }
  };

  const logout = async () => {
    try {
      await fetch(`${BASE}/api/admin/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      /* ignore */
    }
    nav("/admin", { replace: true });
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <div className="text-4xl bob">🛡️</div>
      </div>
    );
  }

  if (status === "denied") {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        data-testid="admin-denied"
      >
        <div className="chunky-card p-6 max-w-md text-center" style={{ background: "#fff" }}>
          <div className="text-5xl mb-2">🚫</div>
          <h1 className="font-display text-2xl" style={{ color: "#FF5A5F" }}>
            Access denied
          </h1>
          <p className="text-sm mt-3" style={{ color: "#484848" }}>
            {error || "This Google account is not the admin."}
          </p>
          <p className="text-xs mt-3" style={{ color: "#8a8a8a" }}>
            For security, your sign-in attempt (your Google account email, name and browser
            info) has been recorded and will be reviewed by the owner. If you reached this
            page by mistake, please close the tab. Repeated attempts may be reported.
          </p>
          <Link to="/admin" className="btn-3d btn-sm mt-4 inline-flex" data-testid="admin-back-to-login">
            Try another account
          </Link>
        </div>
      </div>
    );
  }

  const maxNew = Math.max(1, ...(stats?.daily_new || []).map((d) => d.new_visitors));

  return (
    <AdminShell
      adminEmail={user?.email}
      right={
        <div
          className="inline-flex rounded-full overflow-hidden"
          data-testid="admin-window-picker"
          style={{ border: "3px solid #2b2b2b", background: "#fff" }}
        >
          {WINDOW_OPTIONS.map((opt) => {
            const active = windowDays === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => changeWindow(opt.value)}
                data-testid={`admin-window-${opt.value}-mobile`}
                className="px-3 py-1 font-display text-xs"
                style={{
                  background: active ? "#FFB400" : "#fff",
                  color: "#2b2b2b",
                  borderRight: opt.value !== 90 ? "3px solid #2b2b2b" : "none",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      }
    >
      <div data-testid="admin-dashboard">
      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-6">
        {/* Hero band — welcome + email + quick actions + refresh + time range */}
        <section
          className="chunky-card p-4 sm:p-6 mb-6"
          data-testid="admin-hero"
          style={{
            background:
              "linear-gradient(135deg, #FFB400 0%, #F368E0 60%, #5F27CD 100%)",
            color: "#fff",
          }}
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div
                className="text-xs uppercase tracking-widest font-display opacity-90"
                data-testid="admin-hero-eyebrow"
              >
                67Cafe · Admin
              </div>
              <h1
                className="font-display text-3xl sm:text-4xl mt-1"
                data-testid="admin-hero-title"
              >
                Welcome back <Sparkles size={26} strokeWidth={3} className="inline mb-1" />
              </h1>
              <div
                className="text-sm mt-1 opacity-90 truncate"
                data-testid="admin-user-email"
                title={user?.email}
              >
                Signed in as {user?.email}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div
                className="hidden md:inline-flex rounded-full overflow-hidden"
                data-testid="admin-window-picker-desktop"
                style={{ border: "3px solid #2b2b2b", background: "#fff" }}
              >
                {WINDOW_OPTIONS.map((opt) => {
                  const active = windowDays === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => changeWindow(opt.value)}
                      data-testid={`admin-window-${opt.value}`}
                      className="px-3 py-1 font-display text-xs sm:text-sm"
                      style={{
                        background: active ? "#FFB400" : "#fff",
                        color: "#2b2b2b",
                        borderRight: opt.value !== 90 ? "3px solid #2b2b2b" : "none",
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <button
                className="btn-3d btn-sm"
                onClick={() => loadStats()}
                data-testid="admin-refresh"
                disabled={refreshing}
                aria-label="Refresh stats"
              >
                <RefreshCw
                  size={16}
                  strokeWidth={3}
                  className={refreshing ? "animate-spin" : ""}
                />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4" data-testid="admin-hero-quick-actions">
            <Link
              to="/admin/ads"
              className="btn-3d btn-sm"
              data-testid="admin-hero-new-ad"
            >
              <Megaphone size={16} strokeWidth={3} /> New ad
            </Link>
            <Link
              to="/admin/users"
              className="btn-3d btn-sm btn-purple"
              data-testid="admin-hero-manage-users"
            >
              <Users size={16} strokeWidth={3} /> Manage users
            </Link>
            <button
              type="button"
              onClick={logout}
              className="btn-3d btn-sm btn-danger"
              data-testid="admin-logout"
            >
              <LogOut size={16} strokeWidth={3} /> Sign out
            </button>
          </div>
        </section>

        {/* Grouped KPI grid — single 4-column tier with the four headline
            numbers. Secondary stats hidden behind a "More stats" collapse. */}
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4"
          data-testid="admin-kpis"
        >
          <StatTile
            icon={<Users size={22} strokeWidth={3} />}
            label="Total users"
            value={stats?.total_visitors ?? "—"}
            bg="#FFE0F5"
            testId="stat-total"
          />
          <StatTile
            icon={<Activity size={22} strokeWidth={3} />}
            label="Active today"
            value={stats?.today_active ?? "—"}
            bg="#E5F6FF"
            testId="stat-today"
          />
          <StatTile
            icon={<UserPlus size={22} strokeWidth={3} />}
            label="New this week"
            value={
              stats?.daily_new
                ? stats.daily_new.reduce((s, d) => s + d.new_visitors, 0)
                : "—"
            }
            bg="#E9FFE6"
            testId="stat-week"
          />
          <StatTile
            icon={<Clock size={22} strokeWidth={3} />}
            label="Total playtime"
            value={fmtPlaytime(stats?.totals?.playtime_seconds)}
            bg="#EAF6FF"
            testId="stat-total-playtime"
          />
        </div>

        <details className="mb-6" data-testid="admin-more-stats">
          <summary
            className="cursor-pointer font-display text-sm inline-flex items-center gap-1 chunky-card px-3 py-1"
            style={{ background: "#fff" }}
          >
            More stats <ChevronDown size={16} strokeWidth={3} />
          </summary>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4 mt-3" data-testid="admin-totals">
            <StatTile
              icon={<Gamepad2 size={22} strokeWidth={3} />}
              label="Top game"
              value={stats?.top_games?.[0]?.game_id || "—"}
              bg="#FFF3D6"
              testId="stat-topgame"
            />
            <StatTile
              icon={<Activity size={22} strokeWidth={3} />}
              label="Coins earned"
              value={(stats?.totals?.coins_earned ?? 0).toLocaleString()}
              bg="#FFF3D6"
              testId="stat-coins-earned"
            />
            <StatTile
              icon={<Activity size={22} strokeWidth={3} />}
              label="Coins spent"
              value={(stats?.totals?.coins_spent ?? 0).toLocaleString()}
              bg="#FFECEC"
              testId="stat-coins-spent"
            />
            <StatTile
              icon={<UserPlus size={22} strokeWidth={3} />}
              label="Orders placed"
              value={(stats?.totals?.orders_placed ?? 0).toLocaleString()}
              bg="#E9FFE6"
              testId="stat-orders"
            />
            <StatTile
              icon={<Users size={22} strokeWidth={3} />}
              label="Stickers unlocked"
              value={(stats?.totals?.stickers_unlocked ?? 0).toLocaleString()}
              bg="#EFE7FF"
              testId="stat-stickers"
            />
          </div>
        </details>

        {/* Quick preview strip — mini live preview of each ad slot so the
            admin can see exactly what a kid on that surface would see. */}
        <section
          className="chunky-card p-4 sm:p-5 mb-6"
          data-testid="admin-ads-preview-strip"
          style={{ background: "#fff" }}
        >
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-display text-lg sm:text-xl inline-flex items-center gap-2">
              <Megaphone size={20} strokeWidth={3} /> Live ad slots
            </h2>
            <Link
              to="/admin/ads"
              className="btn-3d btn-sm btn-orange"
              data-testid="admin-ads-preview-manage"
            >
              Manage ads →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { id: "home_hero", label: "Home · hero" },
              { id: "home_secondary", label: "Home · secondary" },
              { id: "games_row", label: "Games · between rows" },
              { id: "games_row_bottom", label: "Games · bottom" },
              { id: "cart_footer", label: "Cart footer" },
              { id: "notifications", label: "Notifications" },
            ].map((slot) => (
              <div
                key={slot.id}
                className="rounded-2xl p-3"
                data-testid={`admin-slot-preview-${slot.id}`}
                style={{
                  background: "#FFF9E6",
                  border: "3px solid #2b2b2b",
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-display text-sm">{slot.label}</span>
                  <code className="text-xs" style={{ color: "#8a8a8a" }}>
                    {slot.id}
                  </code>
                </div>
                <AdBanner slot={slot.id} />
                <EmptySlotHint slotId={slot.id} />
              </div>
            ))}
          </div>
        </section>

        <section
          className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 mb-6"
          data-testid="admin-insights-row"
        >
          <RetentionCard retention={retention} />
          <PlaytimeCard playtime={playtime} />
          <HeatmapCard heatmap={heatmap} />
          <ReferrersCard referrers={referrers} />
        </section>

        <div className="mb-6" data-testid="admin-funnel-wrap">
          <GamesFunnelCard funnel={funnel} />
        </div>

        <section className="chunky-card p-4 sm:p-5 mb-6" data-testid="admin-breakdowns">
          <h2 className="font-display text-lg sm:text-xl mb-3">Audience breakdown</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { key: "devices", title: "Devices", rows: stats?.devices || [] },
              { key: "countries", title: "Countries", rows: stats?.countries || [] },
              { key: "languages", title: "Languages", rows: stats?.languages || [] },
            ].map((b) => (
              <div key={b.key} data-testid={`breakdown-${b.key}`}>
                <div className="font-display text-sm mb-1" style={{ color: "#5F27CD" }}>
                  {b.title}
                </div>
                {b.rows.length === 0 ? (
                  <div className="text-xs" style={{ color: "#8a8a8a" }}>—</div>
                ) : (
                  <ul className="text-sm">
                    {b.rows.map((r) => (
                      <li key={r.key} className="flex items-center justify-between py-0.5">
                        <span style={{ color: "#484848" }}>{r.key}</span>
                        <span className="font-display">{r.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="chunky-card p-4 sm:p-5 mb-6" data-testid="admin-top-fans">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg sm:text-xl flex items-center gap-2">
              🌟 Biggest fans
            </h2>
            <span className="text-xs" style={{ color: "#8a8a8a" }}>
              ranked by playtime + coins earned
            </span>
          </div>
          {(stats?.top_fans || []).length === 0 ? (
            <p className="text-sm" style={{ color: "#484848" }} data-testid="top-fans-empty">
              No visitors have played long enough yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead style={{ background: "#FFB40033" }}>
                  <tr style={{ borderBottom: "2px solid #2b2b2b" }}>
                    <th className="text-left py-2 px-2">#</th>
                    <th className="text-left py-2 px-2">Fan</th>
                    <th className="text-right py-2 px-2">Playtime</th>
                    <th className="text-right py-2 px-2">Coins earned</th>
                    <th className="text-right py-2 px-2">Plays</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.top_fans.map((f, i) => {
                    const secs = f.playtime_seconds || 0;
                    const m = Math.floor(secs / 60);
                    const s = secs % 60;
                    return (
                      <tr
                        key={f.visitor_id}
                        style={{ borderBottom: "1px solid #eee" }}
                        data-testid={`top-fan-row-${i}`}
                      >
                        <td className="py-2 px-2 font-display">
                          {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                        </td>
                        <td className="py-2 px-2">
                          <Link
                            to={`/admin/users/${f.visitor_id}`}
                            style={{ color: "#5F27CD", textDecoration: "underline" }}
                            data-testid={`top-fan-link-${i}`}
                          >
                            {f.last_name || f.first_name || f.visitor_id.slice(0, 10) + "…"}
                          </Link>
                          {f.google_email ? (
                            <span
                              className="ml-2 chunky-badge"
                              data-testid={`top-fan-linked-${i}`}
                              title={`Linked: ${f.google_email}`}
                              style={{
                                background: "#54D62C",
                                color: "#1a1a1a",
                                fontSize: 10,
                                padding: "2px 6px",
                                verticalAlign: "middle",
                              }}
                            >
                              🔗 linked
                            </span>
                          ) : (
                            <span
                              className="ml-2 chunky-badge"
                              data-testid={`top-fan-anon-${i}`}
                              title="Anonymous visitor (no Google account linked)"
                              style={{
                                background: "#eee",
                                color: "#484848",
                                fontSize: 10,
                                padding: "2px 6px",
                                verticalAlign: "middle",
                              }}
                            >
                              anon
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-xs">
                          {m}m {s.toString().padStart(2, "0")}s
                        </td>
                        <td className="py-2 px-2 text-right">
                          {(f.coins_earned || 0).toLocaleString()}
                        </td>
                        <td className="py-2 px-2 text-right">{f.total_plays || 0}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section
          className="chunky-card p-4 sm:p-5 mb-6"
          data-testid="admin-suspicious"
          style={{ background: (suspicious.total || 0) > 0 ? "#FFECEC" : "#fff" }}
        >
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-display text-lg sm:text-xl flex items-center gap-2">
              <ShieldAlert size={22} strokeWidth={3} style={{ color: "#FF5A5F" }} />
              Suspicious sign-in attempts
              <span
                className="chunky-badge"
                style={{
                  background: (suspicious.total || 0) > 0 ? "#FF5A5F" : "#54D62C",
                  color: "#fff",
                  marginLeft: 6,
                }}
                data-testid="suspicious-count"
              >
                {suspicious.total || 0}
              </span>
            </h2>
            {(suspicious.total || 0) > 0 && (
              <button
                type="button"
                className="btn-3d btn-sm btn-danger"
                onClick={clearSuspicious}
                data-testid="suspicious-clear"
              >
                <Trash2 size={14} strokeWidth={3} />
                Clear log
              </button>
            )}
          </div>
          {(suspicious.rows || []).length === 0 ? (
            <p className="text-sm" style={{ color: "#484848" }} data-testid="suspicious-empty">
              No non-admin Google accounts have tried to sign in. 🎉
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead style={{ background: "#FFB40033" }}>
                  <tr style={{ borderBottom: "2px solid #2b2b2b" }}>
                    <th className="text-left py-2 px-2">Email</th>
                    <th className="text-left py-2 px-2">Name</th>
                    <th className="text-left py-2 px-2">At</th>
                    <th className="text-left py-2 px-2">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {suspicious.rows.map((r, i) => (
                    <tr
                      key={`${r.email}-${r.at}-${i}`}
                      style={{ borderBottom: "1px solid #eee" }}
                      data-testid={`suspicious-row-${i}`}
                    >
                      <td className="py-2 px-2 break-all">{r.email || "—"}</td>
                      <td className="py-2 px-2">{r.name || "—"}</td>
                      <td className="py-2 px-2">{(r.at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="py-2 px-2 font-mono text-xs">{r.ip || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="chunky-card p-4 sm:p-5 mb-6" data-testid="admin-chart">
          <h2 className="font-display text-lg sm:text-xl mb-3">New visitors — last 7 days</h2>
          <div
            className="grid grid-cols-7 gap-2 sm:gap-3"
            style={{ height: 180 }}
          >
            {(stats?.daily_new || []).map((d) => {
              const barPx = Math.max(6, Math.round((d.new_visitors / maxNew) * 140));
              return (
                <div key={d.day} className="flex flex-col items-center justify-end h-full">
                  <div
                    className="w-full rounded-t-lg"
                    style={{
                      background: "#5F27CD",
                      border: "3px solid #2b2b2b",
                      height: barPx,
                      transition: "height .3s",
                    }}
                    title={`${d.new_visitors} new`}
                    data-testid={`bar-${d.day}`}
                    data-count={d.new_visitors}
                  />
                  <div className="text-xs mt-1" style={{ color: "#484848" }}>
                    {d.day.slice(5)}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="chunky-card p-4 sm:p-5 mb-6" data-testid="admin-top-games">
          <h2 className="font-display text-lg sm:text-xl mb-3">Top played games</h2>
          {stats?.top_games?.length ? (
            <ul className="grid grid-cols-1 sm:grid-cols-5 gap-3">
              {stats.top_games.map((g) => (
                <li
                  key={g.game_id}
                  className="chunky-card p-3 text-center"
                  data-testid={`top-game-${g.game_id}`}
                  style={{ background: "#FFF9E6" }}
                >
                  <div className="font-display text-sm">{g.game_id}</div>
                  <div className="text-xs" style={{ color: "#484848" }}>
                    {g.plays} plays
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm" style={{ color: "#484848" }}>
              No plays tracked yet.
            </p>
          )}
        </section>

        <section className="chunky-card p-4 sm:p-5" data-testid="admin-recent">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="font-display text-lg sm:text-xl">Latest 50 visitors</h2>
            <div className="flex gap-2 flex-wrap">
              <Link to="/admin/ads" className="btn-3d btn-sm btn-orange" data-testid="admin-manage-ads">
                🎯 Manage ads
              </Link>
              <Link to="/admin/users" className="btn-3d btn-sm btn-purple" data-testid="admin-view-all-users">
                View all users →
              </Link>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: "2px solid #2b2b2b" }}>
                  <th className="text-left py-2">Visitor</th>
                  <th className="text-left py-2">Name</th>
                  <th className="text-left py-2">First seen</th>
                  <th className="text-left py-2">Last seen</th>
                  <th className="text-right py-2">Visits</th>
                  <th className="text-right py-2">Plays</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.recent_visitors || []).map((v) => (
                  <tr key={v.visitor_id} style={{ borderBottom: "1px solid #eee" }}>
                    <td className="py-2 font-mono text-xs">{v.visitor_id.slice(0, 12)}…</td>
                    <td className="py-2">{v.last_name || v.first_name || "—"}</td>
                    <td className="py-2">{(v.first_seen || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="py-2">{(v.last_seen || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="py-2 text-right">{v.visit_count || 0}</td>
                    <td className="py-2 text-right">{v.total_plays || 0}</td>
                  </tr>
                ))}
                {(!stats?.recent_visitors || stats.recent_visitors.length === 0) && (
                  <tr>
                    <td colSpan="6" className="py-3 text-center" style={{ color: "#484848" }}>
                      No visitors yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <MergeDuplicatesCard />

        <StorageSection
          storage={storage}
          storageError={storageError}
          onRetry={loadStats}
          showBackupSteps={showBackupSteps}
          onToggleBackupSteps={() => setShowBackupSteps((v) => !v)}
        />

        <div className="mt-6 text-xs text-center" style={{ color: "#8a8a8a" }}>
          <p>All metrics respect the {windowDays}-day window selector above.</p>
        </div>
      </main>
      </div>
    </AdminShell>
  );
};

const StatTile = ({ icon, label, value, bg, testId }) => (
  <div className="chunky-card p-3 sm:p-4" style={{ background: bg }} data-testid={testId}>
    <div className="flex items-center gap-2" style={{ color: "#5F27CD" }}>
      {icon}
      <span className="text-xs uppercase font-display">{label}</span>
    </div>
    <div className="font-display text-2xl sm:text-3xl mt-1">{value}</div>
  </div>
);

// Compact bytes like '1.2 MB', '640 KB'.
const fmtBytesLocal = (n) => {
  const b = Math.max(0, Number(n) || 0);
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(2)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

// Warning bands: green under 50 MB, amber under 400 MB, red beyond.
const storageBand = (bytes) => {
  const mb = (bytes || 0) / (1024 * 1024);
  if (mb < 50) return { color: "#54D62C", label: "Healthy" };
  if (mb < 400) return { color: "#FFB400", label: "Consider offload" };
  return { color: "#FF5A5F", label: "Offload now" };
};

const StorageSection = ({ storage, storageError, onRetry, showBackupSteps, onToggleBackupSteps }) => {
  if (!storage) {
    return (
      <section
        className="chunky-card p-4 sm:p-5 mb-6"
        data-testid="admin-storage"
        style={{ background: "#fff" }}
      >
        <h2 className="font-display text-lg sm:text-xl mb-2 flex items-center gap-2">
          <HardDrive size={22} strokeWidth={3} style={{ color: "#5F27CD" }} />
          Database storage & backups
        </h2>
        {storageError ? (
          <div data-testid="storage-error" className="text-sm" style={{ color: "#FF5A5F" }}>
            <p className="mb-2">{storageError}</p>
            <button
              type="button"
              onClick={onRetry}
              data-testid="storage-retry-btn"
              className="chunky-btn px-3 py-1 text-xs font-display"
              style={{ background: "#FFB400", color: "#fff" }}
            >
              Retry
            </button>
          </div>
        ) : (
          <p className="text-sm" style={{ color: "#484848" }} data-testid="storage-loading">
            Loading storage insights…
          </p>
        )}
      </section>
    );
  }
  const totalBytes = storage?.totals?.storage_bytes || 0;
  const band = storageBand(totalBytes);
  const cols = Object.entries(storage.collections || {});
  return (
    <section
      className="chunky-card p-4 sm:p-5 mb-6"
      data-testid="admin-storage"
      style={{ background: "#fff" }}
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-display text-lg sm:text-xl flex items-center gap-2">
          <HardDrive size={22} strokeWidth={3} style={{ color: "#5F27CD" }} />
          Database storage & backups
        </h2>
        <span
          className="chunky-badge"
          style={{ background: band.color, color: "#fff" }}
          data-testid="storage-band"
        >
          {band.label}
        </span>
      </div>

      {storageError ? (
        <div
          data-testid="storage-error"
          className="mb-3 rounded-lg px-3 py-2 text-sm flex items-center justify-between gap-2"
          style={{ background: "#FFE9E9", color: "#B00020" }}
        >
          <span>Stale data — {storageError}</span>
          <button
            type="button"
            onClick={onRetry}
            data-testid="storage-retry-btn"
            className="chunky-btn px-3 py-1 text-xs font-display"
            style={{ background: "#FFB400", color: "#fff" }}
          >
            Retry
          </button>
        </div>
      ) : null}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatTile
          icon={<Database size={22} strokeWidth={3} />}
          label="Total DB storage"
          value={fmtBytesLocal(totalBytes)}
          bg="#EFE7FF"
          testId="storage-total-bytes"
        />
        <StatTile
          icon={<Database size={22} strokeWidth={3} />}
          label="Total documents"
          value={(storage?.totals?.documents ?? 0).toLocaleString()}
          bg="#E5F6FF"
          testId="storage-total-docs"
        />
        <StatTile
          icon={<HardDrive size={22} strokeWidth={3} />}
          label="Offload candidates"
          value={(storage?.suggestions || []).length}
          bg="#FFF3D6"
          testId="storage-suggestion-count"
        />
        <StatTile
          icon={<Clock size={22} strokeWidth={3} />}
          label="Snapshot at"
          value={(storage?.generated_at || "").slice(11, 16) || "—"}
          bg="#E9FFE6"
          testId="storage-snapshot-at"
        />
      </div>

      <div className="overflow-x-auto mb-4">
        <table className="w-full text-sm">
          <thead style={{ background: "#FFB40033" }}>
            <tr style={{ borderBottom: "2px solid #2b2b2b" }}>
              <th className="text-left py-2 px-2">Collection</th>
              <th className="text-right py-2 px-2">Docs</th>
              <th className="text-right py-2 px-2">Storage</th>
              <th className="text-right py-2 px-2">Indexes</th>
              <th className="text-right py-2 px-2">Avg doc</th>
            </tr>
          </thead>
          <tbody>
            {cols.map(([name, c]) => (
              <tr
                key={name}
                style={{ borderBottom: "1px solid #eee" }}
                data-testid={`storage-row-${name}`}
              >
                <td className="py-2 px-2 font-display">{name}</td>
                <td className="py-2 px-2 text-right">{(c.count || 0).toLocaleString()}</td>
                <td className="py-2 px-2 text-right font-mono text-xs">
                  {fmtBytesLocal(c.storage_bytes)}
                </td>
                <td className="py-2 px-2 text-right font-mono text-xs">
                  {fmtBytesLocal(c.index_bytes)}
                </td>
                <td className="py-2 px-2 text-right font-mono text-xs">
                  {fmtBytesLocal(c.avg_obj_bytes)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(storage.suggestions || []).length === 0 ? (
        <p
          className="text-sm chunky-card p-3"
          style={{ background: "#E9FFE6", color: "#2b2b2b" }}
          data-testid="storage-suggestions-empty"
        >
          🎉 Nothing to offload right now — your DB looks tidy.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="storage-suggestions">
          {storage.suggestions.map((s) => (
            <li
              key={s.id}
              className="chunky-card p-3"
              style={{ background: "#FFF9E6" }}
              data-testid={`storage-suggestion-${s.id}`}
            >
              <div className="font-display text-sm">{s.title}</div>
              <div className="text-xs mt-1" style={{ color: "#484848" }}>
                {s.detail}
              </div>
              <div className="text-xs mt-1" style={{ color: "#5F27CD" }}>
                Est. reclaim ~ {fmtBytesLocal(s.estimated_reclaim_bytes)}
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4">
        <button
          type="button"
          className="btn-3d btn-sm btn-purple"
          onClick={onToggleBackupSteps}
          data-testid="storage-toggle-backup"
        >
          {showBackupSteps ? (
            <>
              <ChevronUp size={14} strokeWidth={3} /> Hide backup steps
            </>
          ) : (
            <>
              <ChevronDown size={14} strokeWidth={3} /> Show backup steps
            </>
          )}
        </button>
        {showBackupSteps && (
          <ol
            className="mt-3 chunky-card p-3 text-xs space-y-2"
            style={{ background: "#F6F0FF", color: "#2b2b2b" }}
            data-testid="storage-backup-steps"
          >
            {(storage.backup_steps || []).map((step, i) => (
              <li key={i} className="font-mono">
                {step}
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
};

export default AdminDashboard;
